import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Switch,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Typography,
  Box,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { BlockOutlined } from "@mui/icons-material";
import { ArrowCircleRightOutlined } from "@mui/icons-material";
import { BuildCircleOutlined } from "@mui/icons-material";
const IconMAP = {
  AT: <CheckCircleOutline sx={{ color: "#392AB6", mx: "2px" }} />,
  AWV: <BuildCircleOutlined sx={{ color: "#21CCFB", mx: "2px" }} />,
  MV: <ArrowCircleRightOutlined sx={{ color: "#21CCFB", mx: "2px" }} />,
  NA: <BlockOutlined sx={{ color: "#FC3E30", mx: "2px" }} />,
};
const ApplyAdoptionDecisionsDialog = ({
  editable,
  open,
  onClose,
  policyData,
  variant,
  handleAdoptionDecisionSave,
  row,
}) => {
  const [adoptionData, setAdoptionData] = useState([]);

  const handleSwitchChange = (index) => {
    const newData = [...adoptionData];

    newData[index].enabled = !newData[index].enabled;

    if (newData[index].line === "Global" && newData[index].enabled) {
      newData.forEach((row, i) => {
        if (i !== index) {
          row.disabled = true;
          row.enabled = true;
        }
      });
    } else if (newData[index].line === "Global" && !newData[index].enabled) {
      newData.forEach((row, i) => {
        if (i !== index) {
          row.disabled = false;
        }
      });
    }

    setAdoptionData(newData);
  };

  const handleVarianceChange = (index, value) => {
    const newData = [...adoptionData];
    newData[index].variance = value;
    setAdoptionData(newData);
  };

  const handleEndDateChange = (index, value) => {
    const newData = [...adoptionData];
    newData[index].endDate = value;
    setAdoptionData(newData);
  };

  const determineEnabled = (item, lobConfigKey) => {
    if (Array.isArray(lobConfigKey)) {
      return lobConfigKey.some(
        (obj) => obj && Number(obj.LobConfigKey) === item.id
      );
    } else if (lobConfigKey && Number(lobConfigKey.id) === item.id) {
      return true;
    } else if (lobConfigKey && Number(lobConfigKey) === item.id) {
      return true;
    } else if (lobConfigKey === "") {
      return true;
    }
    return false;
  };

  const determineEndDate = (item, lobConfigKey, ruleEndDate) => {
    if (Array.isArray(lobConfigKey)) {
      const found = lobConfigKey.find(
        (obj) => obj && Number(obj.LobConfigKey) === item.id
      );
      return found ? found.endDate : ruleEndDate;
    } else if (lobConfigKey && Number(lobConfigKey.id) === item.id) {
      return lobConfigKey.endDate || "";
    } else if (lobConfigKey && Number(lobConfigKey) === item.id) {
      return lobConfigKey.endDate || "";
    }
    return "";
  };

  useEffect(() => {
    if (policyData) {
      const getDateString = (date) =>
        date ? dayjs(date).format("YYYY-MM-DD") : "";

      const allSameEffectiveDate = policyData.lobAdoptionDecisionList.every(
        (item) =>
          getDateString(item.effectiveDate) ===
          getDateString(policyData.lobAdoptionDecisionList[0].effectiveDate)
      );

      const globalEffectiveDate = allSameEffectiveDate
        ? policyData.lobAdoptionDecisionList[0]?.effectiveDate
        : "";

      const adoptionDataArr = policyData.lobAdoptionDecisionList.map((item) => {
        const effectiveDate = dayjs(item.effectiveDate).format("MM/DD/YYYY");
        const lobActive = determineEnabled(item, row.RuleFacts.LobConfigKey);
        const determineEndDateValue = lobActive
          ? determineEndDate(
              item,
              row.RuleFacts.LobConfigKey,
              row.RuleFacts.RuleEndDate ? row.RuleFacts.RuleEndDate : ""
            )
          : "";
        return {
          line: item.lobDesc,
          id: item.id,
          enabled: lobActive, // use this logic to fix the date issue.
          date: effectiveDate,
          endDate: determineEndDateValue,
          variance: item.varianceComments,
          disabled: false,
          adoptionDecisionDesc: item.adoptionDecisionDesc,
          adoptionDecision: item.adoptionDecision,
        };
      });

      // Add the "Global" value at the beginning
      const globalEntry = {
        line: "Global",
        id: "",
        enabled: row.RuleFacts.LobConfigKey === "",
        date: globalEffectiveDate,
        endDate: "", //in the case of a global rule the end date would be set at the row level.
        variance: "",
        disabled: !allSameEffectiveDate,
      };
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAdoptionData([globalEntry, ...adoptionDataArr]);
    }
  }, [policyData, row]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{ fontWeight: "bold", fontSize: "30px", padding: "16px 24px 0px" }}
      >
        Rule Adoption Decisions
      </DialogTitle>
      <Typography
        variant="body2"
        sx={{ padding: "0px 24px 16px", fontSize: "16px" }}
      >
        Rules can be enabled globally (same effective date for all Lines of
        Business) or by available Lines of Business.
      </Typography>
      <DialogContent>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                align="left"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                }}
              >
                Line of Business
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                }}
              >
                Rule Enabled?
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                }}
              >
                Effective Date
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                  minWidth: "150px",
                }}
              >
                End Date
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                }}
              >
                Variance Comments
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {adoptionData.map((rowData, index) => (
              <TableRow
                key={index}
                sx={{
                  opacity: rowData.disabled ? 0.5 : 1, // Dim the row if disabled
                  pointerEvents: rowData.disabled ? "none" : "auto", // Disable interaction if disabled
                }}
              >
                <TableCell align="left" sx={{ padding: "16px 0" }}>
                  <Box display="flex" alignItems="center">
                    {rowData.line} {IconMAP[rowData.adoptionDecision]}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ padding: "16px 0" }}>
                  <Switch
                    disabled={rowData.disabled || !editable}
                    checked={rowData.enabled}
                    slotProps={{
                      input: {
                        "data-testid": `lob-switch-${rowData.id}`,
                      },
                    }}
                    onChange={() => handleSwitchChange(index)}
                  />
                </TableCell>
                <TableCell align="center" sx={{ padding: "16px 0" }}>
                  {rowData.date ? dayjs(rowData.date).format("MM/DD/YYYY") : ""}
                </TableCell>
                <TableCell align="center" sx={{ padding: "16px 5px" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disabled={
                        rowData.line === "Global" ||
                        !rowData.enabled ||
                        (row.RuleHeader.EditRule &&
                          row.RuleHeader.EditRule !== "EDIT") ||
                        (rowData.line !== "Global" &&
                          row.RuleFacts.LobConfigKey === "")
                      }
                      value={dayjs(rowData.endDate)}
                      onChange={(newValue) =>
                        handleEndDateChange(index, newValue)
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          placeholder: "End Date",
                          sx: { width: "100%" },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </TableCell>
                <TableCell align="center" sx={{ padding: "16px 5px" }}>
                  <TextField
                    value={rowData.variance}
                    onChange={(e) =>
                      handleVarianceChange(index, e.target.value)
                    }
                    size="small"
                    placeholder="Variance Text"
                    disabled={rowData.disabled || !editable}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="AAD_Close"
          id="AAD_Close"
          onClick={onClose}
          sx={{ fontWeight: "bold" }}
        >
          Cancel
        </Button>
        <Button
          data-testid="AAD_Save"
          id="AAD_Save"
          onClick={() => handleAdoptionDecisionSave(adoptionData)}
          sx={{ fontWeight: "bold" }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyAdoptionDecisionsDialog;
